import { Link } from '@tanstack/react-router';
import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * App-wide Markdown → HTML renderer.
 *
 * Parses GitHub-flavored Markdown (incl. tables, via remark-gfm) and renders it with explicit
 * design-system element styles — it does NOT rely on the Tailwind `prose` typography plugin (which
 * isn't installed). Use this anywhere we display model output or stored markdown (AI answers, verify
 * details, etc.) instead of `whitespace-pre-wrap` or a bare <ReactMarkdown>.
 *
 *   <Markdown>{answer}</Markdown>
 *
 * Links: internal paths (href starting with "/") render as a TanStack router <Link> for an SPA
 * transition (no new tab); external http(s)/mailto/tel links open in a new tab
 * (rel="noopener noreferrer"). Headings/paragraphs/lists/code/quote/hr/tables are all themed.
 *
 * URL safety: react-markdown applies its `defaultUrlTransform` to every `href`/`src` unless
 * overridden, which strips dangerous protocols (javascript:, data:, vbscript:, …) while allowing
 * http/https/mailto/tel and relative paths — so `[text](javascript:…)` is neutralised for us.
 *
 * Extension points (used by AlfieContent's citation-token layer):
 *  - `components` — extra/overriding element renderers, shallow-merged over the defaults. Custom
 *    tag names emitted by a rehype plugin (e.g. `citation`) are looked up here.
 *  - `rehypePlugins` — plugins applied after parse (e.g. to rewrite `[[Name]]` tokens in text nodes).
 */
const components: Components = {
  h1: ({ children }) => (
    <h1 className="mb-1 mt-3 text-base font-bold text-foreground first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-1 mt-3 text-sm font-bold text-foreground first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1 mt-2 text-sm font-semibold text-foreground first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="my-1.5 text-sm leading-relaxed text-foreground first:mt-0 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-1.5 ml-4 list-disc space-y-0.5 text-sm text-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-1.5 ml-4 list-decimal space-y-0.5 text-sm text-foreground">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => {
    // Disallowed protocol (javascript:, vbscript:, …) — react-markdown's
    // defaultUrlTransform neutralises these to an empty href. Render the text
    // as a plain span so it isn't a clickable dead anchor.
    if (!href) return <span>{children}</span>;
    // Internal path — SPA transition via the router: no full reload, no new tab.
    // Protocol-relative URLs (//host) are NOT internal — they fall through to
    // the external branch so they can never masquerade as a router path.
    if (href.startsWith('/') && !href.startsWith('//')) {
      return (
        <Link to={href} className="text-primary underline underline-offset-2 hover:opacity-80">
          {children}
        </Link>
      );
    }
    // External (http/https/mailto/tel/…) — open in a new tab.
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:opacity-80"
      >
        {children}
      </a>
    );
  },
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-3 border-border" />,
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-border pl-3 text-sm text-muted-foreground">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    // Block code = fenced (has a language- class) or multi-line; everything else is inline.
    const isBlock = /language-/.test(className ?? '') || String(children).includes('\n');
    if (isBlock) return <code className={className}>{children}</code>;
    return (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded-lg bg-muted p-3 font-mono text-xs text-foreground">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  th: ({ children }) => (
    <th className="border border-border bg-muted/50 px-2 py-1 text-left font-semibold text-foreground">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-border px-2 py-1 align-top text-foreground">{children}</td>
  ),
};

export function Markdown({
  children,
  className,
  components: extraComponents,
  rehypePlugins,
}: {
  children: string;
  className?: string;
  /** Extra / overriding element renderers, shallow-merged over the defaults. */
  components?: Components;
  /** rehype plugins applied after parse (e.g. the citation-token rewriter). */
  rehypePlugins?: Options['rehypePlugins'];
}) {
  const merged: Components = extraComponents
    ? { ...components, ...extraComponents }
    : components;
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins ?? undefined}
        components={merged}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
